/**
 * Backend messages shown on the Ticket Operations pages (Transfer Ticket, Bulk Upload, Refund Management), translated in the `serverTicketOps` namespace
 * (locales/<lng>/serverTicketOps.json). See serverMessages.js for how rules work; `t`, `label` and
 * `nested` here use this file's namespace, and `label` falls back to the shared labels in
 * `server`.
 *
 * The messages come from event-management-service on the backend's main branch:
 * TicketAdminController, TicketServiceImpl (transfer, bulk and manual upload, ticket lookups),
 * RefundServiceImpl and SquarePaymentServiceImpl's refund, and UserServiceImpl.searchUsers.
 * Square's card errors (SquareErrorMapper) and the generic ones are shared rules already.
 */
// eslint-disable-next-line no-unused-vars
const rules = ({ t, label, nested, ANY }) => [
  // ---- Shared by several endpoints below
  { match: 'IBO number is required', key: 'common.iboRequired' },
  { match: /^Event not found with ID: (?<id>\S+)$/, key: 'common.eventNotFoundWithId' },
  // GET em/v1/admin/tickets/users/search, transfer/users: UserServiceImpl.searchUsers
  { match: 'Search term is required', key: 'common.searchTermRequired' },

  // ---- POST em/v1/admin/tickets/transfer, GET transfer/tickets: TicketServiceImpl
  {
    match: /^Failed to validate IBO numbers: Failed to validate IBO: (?<detail>404\b[\s\S]*)$/,
    key: 'transfer.iboNotFoundInAmway',
  },
  {
    match: new RegExp(`^Failed to validate IBO numbers: Failed to validate IBO: (?<detail>${ANY})$`),
    key: 'transfer.amwayCheckFailed',
  },
  {
    match: new RegExp(`^Failed to validate IBO numbers: (?<detail>${ANY})$`),
    key: 'transfer.iboCheckFailed',
  },
  { match: 'Source IBO is not active. Cannot transfer tickets.', key: 'transfer.sourceNotActive' },
  {
    match: 'Source IBO is first contract year. Cannot transfer tickets.',
    key: 'transfer.sourceFirstYear',
  },
  {
    match: 'Destination IBO is not active. Cannot transfer tickets.',
    key: 'transfer.destinationNotActive',
  },
  {
    match: 'Destination IBO is first contract year. Cannot transfer tickets.',
    key: 'transfer.destinationFirstYear',
  },
  {
    match: 'No active tickets found for the source IBO for the specified event',
    key: 'transfer.noActiveSourceTickets',
  },
  {
    match: 'The selected ticket is not an active ticket of the source IBO for this event',
    key: 'transfer.ticketNotActiveForSource',
  },
  { match: "Source IBO doesn't have enough tickets for transfer", key: 'transfer.notEnoughTickets' },
  { match: 'Valid event ID is required', key: 'transfer.eventIdRequired' },

  // ---- POST em/v1/admin/tickets/bulk-upload: TicketAdminController, TicketServiceImpl
  { match: 'Please upload a valid file', key: 'bulkUpload.emptyFile' },
  { match: 'Please upload a CSV or Excel file', key: 'bulkUpload.wrongFileType' },
  {
    match: new RegExp(`^Failed to parse file: (?<detail>${ANY})$`),
    key: 'bulkUpload.parseFailed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },
  {
    // The column names are the ones the backend reads, so they stay in English.
    match: 'Invalid CSV format. Expected columns: Team, IBO Number, Quantity, Customer Name',
    key: 'bulkUpload.invalidCsv',
  },
  {
    match: 'Unsupported file format. Please upload a CSV or Excel file.',
    key: 'bulkUpload.unsupportedFormat',
  },
  { match: 'No valid records found in the uploaded file', key: 'bulkUpload.noRecords' },

  // ---- POST em/v1/admin/tickets/manual-upload (message, and each entry's reason)
  { match: 'At least one entry is required', key: 'bulkUpload.noEntries' },
  { match: 'Ticket already exists for this IBO and event', key: 'bulkUpload.alreadyExists' },
  { match: 'Quantity must be greater than zero', key: 'bulkUpload.quantityPositive' },

  // ---- POST em/v1/admin/tickets/refund: RefundServiceImpl.processRefund
  { match: 'Unable to extract admin email from token', key: 'refund.noAdminEmail' },
  { match: /^Ticket not found with ID: (?<id>\S+)$/, key: 'refund.ticketNotFound' },
  { match: 'Cannot refund a cancelled ticket', key: 'refund.ticketCancelled' },
  { match: 'Cannot refund an expired ticket', key: 'refund.ticketExpired' },
  {
    match: /^Cannot refund (?<requested>\d+) tickets\. Only (?<available>\d+) tickets available\.$/,
    key: 'refund.notEnoughTickets',
  },
  { match: 'Payment record not found for this ticket', key: 'refund.paymentNotFound' },
  {
    match:
      /^Refund for payment method '(?<method>[^']*)' is not supported\. Only CASH and SQUARE refunds are supported\.$/,
    key: 'refund.methodNotSupported',
  },
  { match: 'Invalid payment ID format for CASH payment', key: 'refund.invalidCashPaymentId' },
  // Square's refund, wrapped by RefundServiceImpl; the detail is often a shared card message.
  { match: 'Square refund failed - No refund ID returned', key: 'refund.squareNoRefundId' },
  {
    match: new RegExp(`^Square refund failed: (?<detail>${ANY})$`),
    key: 'refund.squareFailed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },
  // SquarePaymentServiceImpl.processRefund
  { match: 'Refund processing failed - No refund returned', key: 'refund.squareNoRefund' },
  {
    match: new RegExp(`^Failed to process refund: (?<detail>${ANY})$`),
    key: 'refund.processFailed',
    values: ({ detail }) => ({ detail: nested(detail) }),
  },

  // ---- GET em/v1/admin/tickets/refunds: RefundServiceImpl.getAllRefunds
  {
    match: new RegExp(`^Invalid refund status: (?<status>${ANY})$`),
    key: 'refund.invalidStatus',
  },
];

const area = { ns: 'serverTicketOps', rules };

export default area;
